import { Link } from 'react-router-dom';
import { useProjects } from '../../../api/projects';
import { useDonations } from '../../../api/donations';
import { ProjectStatus, DonationStatus } from '@spi/shared';
import type { Donation } from '@spi/shared';
import { StatusBadge } from '../shared/StatusBadge';
import { EmptyState } from '../shared/EmptyState';

export function RecipientDashboard() {
  const { data: myProjects } = useProjects({ limit: 10 });
  const { data: myDonations } = useDonations({ limit: 50 });

  const deliveredDonations = myDonations?.data.filter((t) => t.status === DonationStatus.DELIVERED) ?? [];
  const draftProjects = myProjects?.data.filter((p) => p.status === ProjectStatus.DRAFT) ?? [];
  const actionCount = deliveredDonations.length + draftProjects.length;

  return (
    <div className="space-y-8">
      {/* Action Required */}
      {actionCount > 0 ? (
        <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-amber-800">Action Required</h3>
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold">{actionCount}</span>
          </div>

          {deliveredDonations.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-700 mb-2">Delivered &mdash; Confirm Receipt ({deliveredDonations.length})</p>
              <div className="space-y-2">
                {deliveredDonations.map((donation) => (
                  <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{donation.projectTitle}</p>
                      <p className="text-xs text-spi-slate">From {donation.donorOrganization} &middot; {donation.askCrop} &middot; {donation.quantityKg > 0 ? `${donation.quantityKg}kg` : ''}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Confirm Receipt &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {draftProjects.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-700 mb-2">Draft Projects &mdash; Ready to Publish ({draftProjects.length})</p>
              <div className="space-y-2">
                {draftProjects.map((p) => (
                  <Link key={p.id} to={`/marketplace/projects/${p.id}/edit`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{p.title}</p>
                      <p className="text-xs text-spi-slate">{p.askCount} asks &middot; {p.country}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Publish Project &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : myDonations && myProjects ? (
        <div className="rounded-lg border-l-4 border-spi-green bg-green-50 p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-spi-green flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm font-medium text-green-800">All caught up! No items need your attention right now.</p>
        </div>
      ) : null}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5">
          <p className="text-sm text-spi-slate">My Projects</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{myProjects?.total ?? 0}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Incoming Donations</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{myDonations?.total ?? 0}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Seeds Received (kg)</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">
            {myDonations?.data.reduce((sum: number, t: Donation) => sum + (t.status === 'verified' ? t.quantityKg : 0), 0).toFixed(1) ?? '0'}
          </p>
        </div>
      </div>

      {/* My Projects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl">My Projects</h3>
          <Link to="/marketplace/projects/new" className="btn btn-primary text-sm py-2 px-4">
            Create Project
          </Link>
        </div>
        {myProjects?.data.length ? (
          <div className="space-y-3">
            {myProjects.data.map((project) => (
              <Link key={project.id} to={`/marketplace/projects/${project.id}`} className="card p-4 flex items-center justify-between hover:shadow-lg">
                <div>
                  <h4 className="font-semibold text-spi-charcoal text-base">{project.title}</h4>
                  <p className="text-sm text-spi-slate">
                    {project.askCount} asks &middot; {project.openAskCount} open
                  </p>
                </div>
                <StatusBadge status={project.status} />
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No projects yet"
            description="Create your first project to start receiving seed donations."
            action={<Link to="/marketplace/projects/new" className="btn btn-primary text-sm">Create Project</Link>}
          />
        )}
      </div>

      {/* Incoming Donations */}
      {myDonations?.data.length ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl">Incoming Donations</h3>
            <Link to="/marketplace/donations" className="text-sm text-spi-green font-semibold hover:underline">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {myDonations.data.map((donation) => (
              <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="card p-4 flex items-center justify-between hover:shadow-lg">
                <div>
                  <h4 className="font-semibold text-spi-charcoal text-base">{donation.projectTitle}</h4>
                  <p className="text-sm text-spi-slate">From {donation.donorOrganization} &middot; {donation.askCrop} &middot; {donation.quantityKg > 0 ? `${donation.quantityKg}kg` : ''}</p>
                </div>
                <StatusBadge status={donation.status} />
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
